import React, { useState, useRef, useEffect } from 'react';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { Snackbar} from '@mui/material';
import {
  Box,
  Typography,
  Button,
  LinearProgress,
  Alert,
  Chip,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  CircularProgress,
  Menu,
  Divider,
  Autocomplete,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  InsertDriveFile as FileIcon,
  Close as CloseIcon,
  MoreVert as MoreVertIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { AuditLoggers } from '../../../utils/auditLogger';
import { harmonateTokens as ht } from '../../../theme/harmonateTokens';
import {
  harmonateDialogPaperProps,
  harmonateDialogTitleSx,
  harmonateDialogCloseIconSx,
  harmonateDialogContentSx,
  harmonateProceedButtonSx,
  harmonateCancelButtonSx,
  harmonateFormFieldsSx,
} from '../../../theme/harmonateStyles';

const MultipartFileUpload = ({ onClose, onUploadComplete, category = 'WF', additionalDetails = null, disabled = false }) => {
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    tenantId: localStorage.getItem('tenant_id'),
    unitOfWorkName: 'Test UnitOfWork',
    documentType: '',
    fundId: '',
    investorId: '',
    holdingId: '',
  });
  
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [uploadResults, setUploadResults] = useState([]);
  const [errors, setErrors] = useState([]);
  const [documentTypes, setDocumentTypes] = useState({});
  const [validationErrors, setValidationErrors] = useState({});
  
  // State for API document types (for Excel files)
  const [apiDocumentTypes, setApiDocumentTypes] = useState([]);
  const [loadingDocumentTypes, setLoadingDocumentTypes] = useState(false);

  // State for Source General Ledger systems (for Excel files)
  const [systems, setSystems] = useState([]);
  const [loadingSystems, setLoadingSystems] = useState(false);
  const [selectedSystems, setSelectedSystems] = useState({});
  const [selectedDocType, setSelectedDocType] = useState(null);
  const [documentTypeConfigs, setDocumentTypeConfigs] = useState({});

  // State for PDF document types
  const [pdfDocumentTypes, setPdfDocumentTypes] = useState([]);
  const [loadingPdfDocumentTypes, setLoadingPdfDocumentTypes] = useState(false);
  const [selectedPdfDocumentTypes, setSelectedPdfDocumentTypes] = useState({});

  // State for Funds
  const [funds, setFunds] = useState([]);
  const [loadingFunds, setLoadingFunds] = useState(false);
  const [selectedFunds, setSelectedFunds] = useState({});

  // State for Workflows
  const [workflows, setWorkflows] = useState([]);
  const [loadingWorkflows, setLoadingWorkflows] = useState(false);
  const [selectedWorkflows, setSelectedWorkflows] = useState({});

  // State for templates and preview
  const [templates, setTemplates] = useState({});
  const [fieldMappings, setFieldMappings] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };
  
  const handleSnackbarClose = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [selectedTemplates, setSelectedTemplates] = useState({});

  // State for Preview
  const [previewOpen, setPreviewOpen] = useState(false);
  const [currentPreview, setCurrentPreview] = useState(null);
  const [showDataPreview, setShowDataPreview] = useState(true); // Show data preview by default
  const [customMappings, setCustomMappings] = useState({}); // Store custom field mappings per file
  const [mappingOverrides, setMappingOverrides] = useState({}); // Track overridden auto-mappings per file
  const [previewData, setPreviewData] = useState({
    fileName: '',
    templateName: '',
    excelFields: [],
    templateFields: [],
    mappings: {},
    availableSheets: [],
    selectedSheet: '',
    excelData: []
  });

  // State for menu
  const [anchorEl, setAnchorEl] = useState(null);
  const [currentConfigFile, setCurrentConfigFile] = useState(null);

  // Get API URL from environment or use default
  const API_BASE = process.env.REACT_APP_API_URL;
  const PART_SIZE = 5 * 1024 * 1024; // 5MB

  // Function to dynamically load XLSX library
  const loadXLSX = async () => {
    try {
      // Try to load from CDN first
      const XLSX = await import('https://cdn.sheetjs.com/xlsx-0.20.1/package/xlsx.mjs');
      return XLSX;
    } catch (error) {
      console.warn('Failed to load XLSX from CDN, trying local import:', error);
      // Fallback to local import if CDN fails
      const XLSX = await import('xlsx');
      return XLSX;
    }
  };

  // Get token from localStorage
  const getAuthToken = () => {
    return localStorage.getItem('authToken') || localStorage.getItem('token');
  };

  // Handle menu open
  const handleMenuOpen = (event, fileName) => {
    setAnchorEl(event.currentTarget);
    setCurrentConfigFile(fileName);
  };

  // Handle menu close
  const handleMenuClose = () => {
    setAnchorEl(null);
    setCurrentConfigFile(null);
  };

  // Fetch document types from API (for Excel - REF category, but use category prop for TR and GL)
  const fetchDocumentTypes = async () => {
    setLoadingDocumentTypes(true);
    try {
      const token = getAuthToken();
      const tenantId = localStorage.getItem('tenant_id');
      if (!tenantId) {
        throw new Error('Tenant ID not found');
      }
      // Use category prop for TR and GL, otherwise use REF for other pages
      const categoryToUse = (category === 'TR' || category === 'GL') ? category : 'REF';
      const url = new URL(`${process.env.REACT_APP_BACKEND_URL}conductor/document-type-tenant-configs/tenant/${tenantId}`);
      url.searchParams.append('category', categoryToUse);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch document types: ${response.status}`);
      }
      
      const data = await response.json();
      const formattedDocTypes = Array.isArray(data)
        ? data.map((item) => ({
            id: item.id,
            documentTypeId: item.documentTypeId,
            name: item.documentTypeName || item.name || '',
            configId: item.id,
            ...item,
          })).filter((item) => item.documentTypeId)
        : [];
      setApiDocumentTypes(formattedDocTypes);
    } catch (error) {
      console.error('Error fetching document types:', error);
      setErrors(prev => [...prev, `Failed to load document types: ${error.message}`]);
    } finally {
      setLoadingDocumentTypes(false);
    }
  };

  // Fetch PDF document types from tenant-specific endpoint
  const fetchPdfDocumentTypes = async () => {
    setLoadingPdfDocumentTypes(true);
    try {
      const token = getAuthToken();
      const tenantId = localStorage.getItem('tenant_id');
      
      if (!tenantId) {
        throw new Error('Tenant ID not found');
      }
      
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}conductor/document-type-tenant-configs/tenant/${tenantId}?category=${category}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (!response.ok) {
        throw new Error(`Failed to fetch PDF document types: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Create a mapping of document type IDs to their full configs
      const configsMap = {};
      data.forEach(item => {
        configsMap[item.documentTypeId] = item;
      });
      
      setDocumentTypeConfigs(configsMap);
      
      // Map the response to match the expected format for the dropdown
      const formattedDocTypes = data.map(item => ({
        id: item.documentTypeId,
        configId: item.id,
        documentTypeId: item.documentTypeId,
        name: item.documentTypeName,
        ...item
      }));
      
      setPdfDocumentTypes(formattedDocTypes);
    } catch (error) {
      console.error('Error fetching PDF document types:', error);
      setErrors(prev => [...prev, `Failed to load PDF document types: ${error.message}`]);
    } finally {
      setLoadingPdfDocumentTypes(false);
    }
  };

  // Fetch funds for the current tenant
  const fetchFunds = async () => {
    setLoadingFunds(true);
    try {
      const token = getAuthToken();
      const tenantId = localStorage.getItem('tenant_id');
      
      if (!tenantId) {
        throw new Error('Tenant ID not found');
      }
      
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}conductor/funds/by-tenant-entity?tenantId=${tenantId}`, 
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (!response.ok) {
        throw new Error(`Failed to fetch funds: ${response.status}`);
      }
      
      const data = await response.json();
      const formattedFunds = data
        .filter(fund => fund.fundName || fund.fundCode) // Only include items with fund name or code
        .map(fund => ({
          id: fund.id,
          name: fund.fundName || `Fund ${fund.id || ''}`,
          code: fund.fundCode || '',
          ...fund
        }))
        .sort((a, b) => a.name.localeCompare(b.name)); // Sort funds alphabetically by name
      
      setFunds(formattedFunds);
    } catch (error) {
      console.error('Error fetching funds:', error);
      setErrors(prev => [...prev, `Failed to load funds: ${error.message}`]);
    } finally {
      setLoadingFunds(false);
    }
  };

  // Fetch systems for Source General Ledger (for Excel files)
  const fetchSystems = async () => {
    setLoadingSystems(true);
    try {
      const token = getAuthToken();
      const tenantId = localStorage.getItem('tenant_id');
      if (!tenantId) {
        throw new Error('Tenant ID not found');
      }
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}conductor/tenant-systems/by-tenant?tenant_id=${tenantId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch systems: ${response.status}`);
      }
      
      const data = await response.json();
      setSystems(data);
    } catch (error) {
      console.error('Error fetching systems:', error);
      setErrors(prev => [...prev, `Failed to load systems: ${error.message}`]);
    } finally {
      setLoadingSystems(false);
    }
  };

  // Fetch templates based on document type and GL system
  const fetchTemplates = async (fileName, docTypeId, systemId) => {
    if (!docTypeId || !systemId) return;
    
    setLoadingTemplates(true);
    try {
      const token = getAuthToken();
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}conductor/ref-templates/by-document-and-gl?documentTypeId=${docTypeId}&glSystemId=${systemId}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (!response.ok) {
        throw new Error(`Failed to fetch templates: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Transform the data to match our expected format
      const transformedTemplates = Array.isArray(data) ? data.map(template => ({
        id: template.templateId,
        name: template.templateName,
        description: template.description,
        version: template.version,
        mappings: template.mapping || {}
      })) : [];
      
      setTemplates(prev => ({
        ...prev,
        [fileName]: transformedTemplates
      }));
    } catch (error) {
      console.error('Error fetching templates:', error);
      setErrors(prev => [...prev, `Failed to load templates: ${error.message}`]);
    } finally {
      setLoadingTemplates(false);
    }
  };

  // Handle template selection change
  const handleTemplateChange = (fileName, templateId) => {
    setSelectedTemplates(prev => ({
      ...prev,
      [fileName]: templateId
    }));
    
    setValidationErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[`${fileName}-template`];
      return newErrors;
    });
  };
  
  // Handle preview button click
  const handlePreviewClick = async (file) => {
    const templateId = selectedTemplates[file.name];
    if (!templateId) return;
    
    try {
      const template = (templates[file.name] || []).find(t => t.id === templateId);
      
      if (!template) {
        throw new Error('Selected template not found');
      }
      
      const XLSX = await loadXLSX();
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: 'array' });
      
      const metadataSheets = ['STATIC_DATA', 'Review >>', 'Supporting>>'];
      const dataSheets = workbook.SheetNames.filter(sheet => 
        !metadataSheets.includes(sheet) && 
        !sheet.toLowerCase().includes('static') &&
        !sheet.includes('>>')
      );
      
      let selectedSheetName = workbook.SheetNames.find(sheet => sheet === 'Summary') || 
                           (dataSheets.length > 0 ? dataSheets[0] : workbook.SheetNames[0]);
      
      const { fields: excelFields, data: excelData } = extractFieldsFromSheet(XLSX, workbook, selectedSheetName);
      
      // Build field mappings including auto-mapped fields
      const fieldMappings = {};
      const templateFields = [];
      const autoMappedFields = [];
      
      if (template.mappings) {
        Object.entries(template.mappings).forEach(([sourceField, mapping]) => {
          if (mapping && mapping.isActive !== false) {
            const matchedField = excelFields.find(field => 
              field.toLowerCase() === sourceField.toLowerCase()
            );
            
            if (matchedField) {
              fieldMappings[sourceField] = {
                targetColumn: mapping.targetColumn || sourceField,
                dataType: mapping.dataType || 'TEXT',
                isRequired: mapping.isRequired || false,
                excelField: matchedField,
                mappingType: 'auto' // Track auto-mapped fields
              };
              templateFields.push(sourceField);
              autoMappedFields.push(sourceField);
            }
          }
        });
      }
      
      // Apply any existing custom mappings for this file
      const existingCustomMappings = customMappings[file.name] || {};
      Object.entries(existingCustomMappings).forEach(([excelField, templateField]) => {
        if (!fieldMappings[templateField] && excelFields.includes(excelField)) {
          const mapping = template.mappings?.[templateField];
          fieldMappings[templateField] = {
            targetColumn: mapping?.targetColumn || templateField,
            dataType: mapping?.dataType || 'TEXT',
            isRequired: mapping?.isRequired || false,
            excelField: excelField,
            mappingType: 'custom' // Track custom-mapped fields
          };
          if (!templateFields.includes(templateField)) {
            templateFields.push(templateField);
          }
        }
      });
      
      // Process excel data with all mappings (auto + custom)
      const processExcelData = (data) => {
        return data.map(row => {
          const mappedRow = {};
          templateFields.forEach(field => {
            const mapping = fieldMappings[field];
            if (mapping?.excelField && row[mapping.excelField] !== undefined) {
              mappedRow[field] = convertValue(row[mapping.excelField], mapping.dataType);
            }
          });
          return mappedRow;
        });
      };
      
      const processedData = processExcelData(excelData);
      
      setCurrentPreview({ 
        file, 
        template, 
        workbook, 
        XLSX,
        excelData: processedData,
        fieldMappings,
        templateFields,
        dataSheets,
        autoMappedFields // Store which fields were auto-mapped
      });
      
      setShowDataPreview(true); 
      setPreviewData(prev => ({
        ...prev,
        fileName: file.name,
        templateName: template.name || 'Unnamed Template',
        excelFields: excelFields,
        templateFields: templateFields,
        mappings: fieldMappings,
        availableSheets: dataSheets,
        selectedSheet: selectedSheetName,
        excelData: processedData,
        autoMappedFields // Pass to preview data
      }));
      
      setPreviewOpen(true);
    } catch (error) {
      console.error('Error generating preview:', error);
      setErrors(prev => [...prev, `Failed to generate preview: ${error.message}`]);
    }
  };
  
  // Handle custom field mapping between Excel columns and template fields
  const handleCustomMapping = (excelField, templateField, previousTemplateField = null) => {
    if (!currentPreview) return;
    
    const { file, template, workbook, XLSX, dataSheets } = currentPreview;
    const currentSheet = previewData.selectedSheet;
    
    // Update custom mappings state
    setCustomMappings(prev => {
      const fileCustomMappings = { ...(prev[file.name] || {}) };
      
      // Remove any previous mapping for this excel field
      Object.keys(fileCustomMappings).forEach(key => {
        if (key === excelField) {
          delete fileCustomMappings[key];
        }
      });
      
      // If there was a previous template field being changed, we need to track it
      if (previousTemplateField) {
        // Remove the previous mapping from any excel field
        Object.keys(fileCustomMappings).forEach(key => {
          if (fileCustomMappings[key] === previousTemplateField) {
            delete fileCustomMappings[key];
          }
        });
      }
      
      // Add new mapping if templateField is provided (empty string means unmapping)
      if (templateField && templateField !== '') {
        fileCustomMappings[excelField] = templateField;
      }
      
      return {
        ...prev,
        [file.name]: fileCustomMappings
      };
    });
    
    // Track if this is overriding an auto-mapping
    if (previousTemplateField) {
      setMappingOverrides(prev => {
        const fileOverrides = { ...(prev[file.name] || {}) };
        
        // Check if previous mapping was auto-mapped
        const wasAutoMapped = previewData.autoMappedFields?.includes(previousTemplateField);
        
        if (wasAutoMapped && templateField && templateField !== '') {
          // Mark this template field as having an overridden auto-mapping
          fileOverrides[previousTemplateField] = 'overridden';
        } else if (wasAutoMapped && (!templateField || templateField === '')) {
          // Auto-mapping was cleared
          fileOverrides[previousTemplateField] = 'cleared';
        }
        
        return {
          ...prev,
          [file.name]: fileOverrides
        };
      });
    }
    
    // Re-extract sheet data
    const { fields: excelFields, data: excelData } = extractFieldsFromSheet(XLSX, workbook, currentSheet);
    
    // Rebuild field mappings
    const newFieldMappings = {};
    const newTemplateFields = [];
    const autoMappedFields = [];
    
    // First, add auto-mapped fields
    if (template.mappings) {
      Object.entries(template.mappings).forEach(([sourceField, mapping]) => {
        if (mapping && mapping.isActive !== false) {
          const matchedField = excelFields.find(field => 
            field.toLowerCase() === sourceField.toLowerCase()
          );
          
          if (matchedField) {
            newFieldMappings[sourceField] = {
              targetColumn: mapping.targetColumn || sourceField,
              dataType: mapping.dataType || 'TEXT',
              isRequired: mapping.isRequired || false,
              excelField: matchedField,
              mappingType: 'auto'
            };
            newTemplateFields.push(sourceField);
            autoMappedFields.push(sourceField);
          }
        }
      });
    }
    
    // Then, add custom mappings (including the new one)
    const updatedCustomMappings = { ...(customMappings[file.name] || {}) };
    if (templateField) {
      updatedCustomMappings[excelField] = templateField;
    } else {
      delete updatedCustomMappings[excelField];
    }
    
    Object.entries(updatedCustomMappings).forEach(([exField, tempField]) => {
      if (!newFieldMappings[tempField]) {
        const mapping = template.mappings?.[tempField];
        newFieldMappings[tempField] = {
          targetColumn: mapping?.targetColumn || tempField,
          dataType: mapping?.dataType || 'TEXT',
          isRequired: mapping?.isRequired || false,
          excelField: exField,
          mappingType: 'custom'
        };
        if (!newTemplateFields.includes(tempField)) {
          newTemplateFields.push(tempField);
        }
      }
    });
    
    // Process data with updated mappings
    const processExcelData = (data) => {
      return data.map(row => {
        const mappedRow = {};
        newTemplateFields.forEach(field => {
          const mapping = newFieldMappings[field];
          if (mapping?.excelField && row[mapping.excelField] !== undefined) {
            mappedRow[field] = convertValue(row[mapping.excelField], mapping.dataType);
          }
        });
        return mappedRow;
      });
    };
    
    const processedData = processExcelData(excelData);
    
    // Update preview data
    setPreviewData(prev => ({
      ...prev,
      templateFields: newTemplateFields,
      mappings: newFieldMappings,
      excelData: processedData,
      autoMappedFields
    }));
    
    // Update current preview
    setCurrentPreview(prev => ({
      ...prev,
      excelData: processedData,
      fieldMappings: newFieldMappings,
      templateFields: newTemplateFields,
      autoMappedFields
    }));
  };

  // Convert value based on data type with better handling and validation
  const convertValue = (value, dataType) => {
    // Handle empty values
    if (value === '' || value === null || value === undefined) {
      return null;
    }
    
    // Convert to string and trim for consistent processing
    const strValue = String(value).trim();
    
    try {
      const type = (dataType || 'TEXT').toUpperCase();
      
      switch (type) {
        case 'NUMBER':
        case 'INTEGER':
        case 'FLOAT':
        case 'DECIMAL':
          // Handle different number formats and clean up any non-numeric characters except decimal point and minus
          const numValue = strValue.replace(/[^0-9.-]/g, '');
          return numValue === '' ? null : Number(numValue);
          
        case 'BOOLEAN':
          // Handle various boolean representations
          const lowerValue = strValue.toLowerCase();
          if (['true', 'yes', 'y', '1', 't'].includes(lowerValue)) return true;
          if (['false', 'no', 'n', '0', 'f'].includes(lowerValue)) return false;
          return Boolean(strValue); // Fallback to truthy/falsy
          
        case 'DATE':
          const date = new Date(strValue);
          // If date is invalid, try parsing Excel date number
          if (isNaN(date.getTime()) && /^\d+(\.\d+)?$/.test(strValue)) {
            // Excel date is the number of days since 1900-01-01
            const excelDate = parseFloat(strValue);
            const utcDays = Math.floor(excelDate - 25569); // 25569 = days from 1900-01-01 to 1970-01-01
            const utcValue = utcDays * 86400 * 1000; // Convert days to milliseconds
            const dateObj = new Date(utcValue);
            return isNaN(dateObj.getTime()) ? null : dateObj.toISOString().split('T')[0];
          }
          return isNaN(date.getTime()) ? strValue : date.toISOString().split('T')[0];
          
        case 'DATETIME':
          const datetime = new Date(strValue);
          // If datetime is invalid, try parsing Excel datetime number
          if (isNaN(datetime.getTime()) && /^\d+(\.\d+)?$/.test(strValue)) {
            // Excel datetime is the number of days since 1900-01-01
            const excelDate = parseFloat(strValue);
            const utcDays = Math.floor(excelDate - 25569); // 25569 = days from 1900-01-01 to 1970-01-01
            const utcValue = utcDays * 86400 * 1000; // Convert days to milliseconds
            const dateObj = new Date(utcValue);
            return isNaN(dateObj.getTime()) ? null : dateObj.toISOString();
          }
          return isNaN(datetime.getTime()) ? strValue : datetime.toISOString();
          
        case 'TEXT':
        default:
          // For text fields, clean up any extra whitespace
          return strValue.replace(/\s+/g, ' ').trim();
      }
    } catch (e) {
      console.warn(`Error converting value "${value}" to ${dataType}:`, e);
      return strValue; // Return original string if conversion fails
    }
  };

  // Helper function to extract and map Excel data to JSON based on template mappings
  const extractMappedDataFromExcel = async (file, templateId) => {
    try {
      const template = (templates[file.name] || []).find(t => t.id === templateId);
      if (!template) {
        throw new Error('Template not found');
      }

      const XLSX = await loadXLSX();
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: 'array' });
      
      const selectedSheetName = previewData?.selectedSheet || workbook.SheetNames[0];
      const worksheet = workbook.Sheets[selectedSheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' });
      
      if (!jsonData || jsonData.length === 0) {
        return [];
      }
      
      const headers = jsonData[0].map(h => h ? h.toString().trim() : '').filter(h => h !== '');
      
      // Build field mappings including custom mappings
      const fieldMappings = {};
      const templateFields = [];
      
      // Add auto-mapped fields from template
      if (template.mappings) {
        Object.entries(template.mappings).forEach(([sourceField, mapping]) => {
          if (mapping && mapping.isActive !== false) {
            const matchedField = headers.find(field => 
              field.toLowerCase() === sourceField.toLowerCase()
            );
            
            if (matchedField) {
              fieldMappings[sourceField] = {
                targetColumn: mapping.targetColumn || sourceField,
                dataType: mapping.dataType || 'TEXT',
                isRequired: mapping.isRequired || false,
                excelField: matchedField,
                mappingType: 'auto'
              };
              templateFields.push(sourceField);
            }
          }
        });
      }
      
      // Add custom mappings
      const fileCustomMappings = customMappings[file.name] || {};
      Object.entries(fileCustomMappings).forEach(([excelField, templateField]) => {
        if (!fieldMappings[templateField] && headers.includes(excelField)) {
          const mapping = template.mappings?.[templateField];
          fieldMappings[templateField] = {
            targetColumn: mapping?.targetColumn || templateField,
            dataType: mapping?.dataType || 'TEXT',
            isRequired: mapping?.isRequired || false,
            excelField: excelField,
            mappingType: 'custom'
          };
          if (!templateFields.includes(templateField)) {
            templateFields.push(templateField);
          }
        }
      });
      
      // Convert rows to mapped objects with proper data types
      const mappedData = [];
      for (let i = 1; i < jsonData.length; i++) {
        const row = {};
        let hasData = false;
        
        Object.entries(fieldMappings).forEach(([sourceField, mapping]) => {
          const headerIndex = headers.indexOf(mapping.excelField);
          if (headerIndex !== -1 && jsonData[i][headerIndex] !== undefined) {
            row[sourceField] = convertValue(jsonData[i][headerIndex], mapping.dataType);
            if (row[sourceField] !== '' && row[sourceField] !== null) {
              hasData = true;
            }
          } else if (mapping.isRequired) {
            // For required fields that are missing, include them as null
            row[sourceField] = null;
          }
        });
        
        if (hasData) {
          mappedData.push(row);
        }
      }
      
      return mappedData;
    } catch (error) {
      console.error('Error extracting mapped data:', error);
      throw error;
    }
  };

  // Handle applying the preview and saving the field mappings
  const handlePreviewApply = () => {
    if (!currentPreview) return;
    
    const { fileName, template } = currentPreview;
    
    // Update the fieldMappings state with the current mappings
    setFieldMappings(prev => ({
      ...prev,
      [fileName]: {
        ...prev[fileName],
        ...previewData.mappings
      }
    }));
    
    // Close the preview dialog
    setCurrentPreview(null);
    setShowDataPreview(false);
    
    // Show success message
    showSnackbar('Field mappings applied successfully', 'success');
  };

  // Get available template fields for mapping
  const getAvailableTemplateFields = (currentExcelField, currentMappedTemplateField) => {
    if (!currentPreview?.template) return [];
    
    const { template } = currentPreview;
    const allTemplateFields = Object.keys(template.mappings || {})
      .filter(field => {
        const mapping = template.mappings[field];
        return mapping && mapping.isActive !== false;
      });
    
    // Get currently used template fields from mappings
    const usedFields = new Set();
    Object.entries(previewData.mappings || {}).forEach(([templateField, mapping]) => {
      // Don't include the current field's mapping in used fields
      if (mapping.excelField && mapping.excelField !== currentExcelField) {
        usedFields.add(templateField);
      }
    });
    
    // Return all fields that are either unused or currently selected
    return allTemplateFields
      .filter(field => !usedFields.has(field) || field === currentMappedTemplateField)
      .sort((a, b) => {
        // Sort required fields first, then alphabetically
        const aRequired = template.mappings[a]?.isRequired ? 0 : 1;
        const bRequired = template.mappings[b]?.isRequired ? 0 : 1;
        
        if (aRequired !== bRequired) {
          return aRequired - bRequired;
        }
        
        return a.localeCompare(b);
      });
  };

  // Extract fields and data from a specific sheet
  const extractFieldsFromSheet = (XLSX, workbook, sheetName) => {
    const worksheet = workbook.Sheets[sheetName];
    
    // Convert to JSON to get all rows
    const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' });
    
    console.log(`Raw Excel data from sheet "${sheetName}" (first 3 rows):`, jsonData.slice(0, 3));
    
    if (!jsonData || jsonData.length === 0) {
      return { fields: [], data: [] };
    }
    
    // Find the first non-empty row as headers
    let headerRowIndex = 0;
    for (let i = 0; i < Math.min(jsonData.length, 10); i++) {
      const row = jsonData[i];
      const nonEmptyCells = row.filter(cell => cell !== null && cell !== undefined && cell !== '');
      if (nonEmptyCells.length > 0) {
        headerRowIndex = i;
        break;
      }
    }
    
    // Extract headers from the found row
    const fields = jsonData[headerRowIndex]
      .map(header => {
        if (header === null || header === undefined) return '';
        return header.toString().trim();
      })
      .filter(header => header !== '');
    
    console.log(`Found ${fields.length} headers in row ${headerRowIndex}:`, fields);
    
    // Convert ALL rows to objects with field names as keys
    const data = [];
    for (let i = headerRowIndex + 1; i < jsonData.length; i++) {
      const row = {};
      let hasData = false;
      
      jsonData[i].forEach((value, index) => {
        if (fields[index]) {
          row[fields[index]] = value;
          if (value !== null && value !== undefined && value !== '') {
            hasData = true;
          }
        }
      });
      
      // Only include rows with at least some data
      if (hasData) {
        data.push(row);
      }
    }
    
    console.log(`Extracted ${data.length} data rows from "${sheetName}"`);
    if (data.length > 0) {
      console.log('Sample data row:', data[0]);
      if (data.length > 1) {
        console.log('Last data row:', data[data.length - 1]);
      }
    }
    
    return { fields, data };
  };
  
  // Handle sheet change in preview
  const handleSheetChange = (newSheetName) => {
    if (!currentPreview) return;
    
    const { workbook, XLSX, template, fieldMappings: prevFieldMappings, templateFields: prevTemplateFields } = currentPreview;
    
    // Extract fields and data from the new sheet
    const { fields: excelFields, data: excelData } = extractFieldsFromSheet(XLSX, workbook, newSheetName);
    
    // Re-process mappings for new sheet
    const newFieldMappings = {};
    const newTemplateFields = [];
    
    if (template.mappings) {
      Object.entries(template.mappings).forEach(([sourceField, mapping]) => {
        if (mapping && mapping.isActive !== false) {
          const matchedField = excelFields.find(field => 
            field.toLowerCase() === sourceField.toLowerCase()
          );
          
          newFieldMappings[sourceField] = {
            targetColumn: mapping.targetColumn || sourceField,
            dataType: mapping.dataType || 'TEXT',
            isRequired: mapping.isRequired || false,
            excelField: matchedField || null
          };
          
          if (matchedField) {
            newTemplateFields.push(sourceField);
          }
        }
      });
    }
    
    // Process the data with new mappings
    const processExcelData = (data) => {
      return data.map(row => {
        const mappedRow = {};
        newTemplateFields.forEach(field => {
          const mapping = newFieldMappings[field];
          if (mapping?.excelField) {
            mappedRow[field] = row[mapping.excelField];
          }
        });
        return mappedRow;
      });
    };
    
    const processedData = processExcelData(excelData);
    
    // Update preview data
    setPreviewData(prev => ({
      ...prev,
      excelFields,
      templateFields: newTemplateFields,
      mappings: newFieldMappings,
      selectedSheet: newSheetName,
      excelData: processedData
    }));
    
    // Update current preview
    setCurrentPreview(prev => ({
      ...prev,
      excelData: processedData,
      fieldMappings: newFieldMappings,
      templateFields: newTemplateFields
    }));
  };

  // Handle system selection change (for Excel files)
  const handleSystemChange = async (fileName, systemId) => {
    setSelectedSystems(prev => ({
      ...prev,
      [fileName]: systemId
    }));
    
    // Reset selected template when system changes
    setSelectedTemplates(prev => {
      const newTemplates = { ...prev };
      delete newTemplates[fileName];
      return newTemplates;
    });
    
    // Fetch templates if document type is already selected
    const docTypeId = documentTypes[fileName];
    if (docTypeId && systemId) {
      await fetchTemplates(fileName, docTypeId, systemId);
    }
    
    setValidationErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[`${fileName}-type`];
      delete newErrors[`${fileName}-system`];
      delete newErrors[`${fileName}-template`];
      return newErrors;
    });
  };

  // Handle PDF document type change
  const handlePdfDocumentTypeChange = async (fileName, docTypeId) => {
    setSelectedPdfDocumentTypes(prev => ({
      ...prev,
      [fileName]: docTypeId
    }));
    
    // Reset selected workflow when document type changes
    setSelectedWorkflows(prev => ({
      ...prev,
      [fileName]: ''
    }));
    
    // Fetch workflows for the selected document type
    if (docTypeId) {
      await fetchWorkflows(docTypeId);
    }
    
    setValidationErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[`${fileName}-pdf-type`];
      delete newErrors[`${fileName}-fund`];
      return newErrors;
    });
  };

  // Handle fund selection change
  const handleFundChange = (fileName, fundId) => {
    setSelectedFunds(prev => ({
      ...prev,
      [fileName]: fundId
    }));
    
    setValidationErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[`${fileName}-pdf-type`];
      delete newErrors[`${fileName}-fund`];
      return newErrors;
    });
  };

  // Handle workflow selection change
  const handleWorkflowChange = (fileName, workflowId) => {
    const selectedWorkflow = workflows.find(w => w.id === workflowId);
    
    if (!selectedWorkflow) {
      console.warn(`No workflow found with id: ${workflowId}`);
      return;
    }
    
    setSelectedWorkflows(prev => ({
      ...prev,
      [fileName]: selectedWorkflow
    }));
    
    setValidationErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[`${fileName}-workflow`];
      return newErrors;
    });
  };

  // Fetch workflows based on document type
  const fetchWorkflows = async (documentTypeId) => {
    if (!documentTypeId) {
      return [];
    }
    
    setLoadingWorkflows(true);
    try {
      const token = getAuthToken();
      const tenantId = localStorage.getItem('tenant_id');
      const baseUrl = (process.env.REACT_APP_BACKEND_URL || 'http://localhost:4002').replace(/\/+$/, '');
      
      const docTypeConfig = pdfDocumentTypes.find(dt => dt.id === documentTypeId);
      const docTypeIdToUse = docTypeConfig?.documentTypeId || documentTypeId;
      const url = `${baseUrl}/conductor/tenant-document-type-workflow-snowflake/tenant/${tenantId}/document-type/${docTypeIdToUse}`;

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const workflows = Array.isArray(data) ? data : [];
      setWorkflows(workflows);
      return workflows;
    } catch (error) {
      console.error('Error fetching workflows:', error);
      setErrors(prev => [...prev, `Failed to load workflows: ${error.message}`]);
      return [];
    } finally {
      setLoadingWorkflows(false);
    }
  };

  // Fetch data on component mount
  useEffect(() => {
    fetchDocumentTypes();
    fetchSystems();
    fetchPdfDocumentTypes();
    fetchFunds();
  }, []);

  const getEmptyFileMessage = (fileName) =>
    `"${fileName}" is empty (0 bytes) and cannot be uploaded. Please select a document that contains data.`;

  const addFiles = (incomingFiles) => {
    const emptyFiles = [];
    const validFiles = [];

    incomingFiles.forEach((file) => {
      if (!file || file.size === 0) {
        emptyFiles.push(file);
      } else {
        validFiles.push(file);
      }
    });

    setUploadResults([]);

    if (emptyFiles.length > 0) {
      const emptyMessages = emptyFiles.map((file) =>
        getEmptyFileMessage(file?.name || 'Selected file')
      );
      setErrors(emptyMessages);
      showSnackbar(
        emptyFiles.length === 1
          ? emptyMessages[0]
          : `${emptyFiles.length} empty files (0 bytes) were rejected and cannot be uploaded.`,
        'error'
      );
    } else {
      setErrors([]);
    }

    if (validFiles.length === 0) {
      return;
    }

    setFiles((prevFiles) => [...prevFiles, ...validFiles]);

    const initialProgress = {};
    const initialDocumentTypes = {};
    validFiles.forEach((file) => {
      initialProgress[file.name] = 0;
      initialDocumentTypes[file.name] = getFileType(file);
    });
    setUploadProgress((prev) => ({ ...prev, ...initialProgress }));
    setDocumentTypes((prev) => ({ ...prev, ...initialDocumentTypes }));
  };

  const handleFileSelect = (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    addFiles(selectedFiles);
    // Allow re-selecting the same file after rejection
    event.target.value = '';
  };

  const handleFileDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const droppedFiles = Array.from(event.dataTransfer.files || []);
    addFiles(droppedFiles);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileType = (file) => {
    return null;
  };

  // Handle document type change (for Excel files)
  const handleDocumentTypeChange = async (fileName, docTypeId) => {
    setDocumentTypes(prev => ({
      ...prev,
      [fileName]: docTypeId
    }));
    
    // Reset selected template when document type changes
    setSelectedTemplates(prev => {
      const newTemplates = { ...prev };
      delete newTemplates[fileName];
      return newTemplates;
    });
    
    setValidationErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[`${fileName}-type`];
      delete newErrors[`${fileName}-template`];
      return newErrors;
    });
    
    // If system is already selected, fetch templates
    const systemId = selectedSystems[fileName];
    if (docTypeId && systemId) {
      await fetchTemplates(fileName, docTypeId, systemId);
    }
    
    if (docTypeId) {
      const docType = pdfDocumentTypes.find(dt => dt.id === docTypeId);
      if (docType) {
        setSelectedDocType(docType);
        fetchWorkflows(docType.id);
      }
    } else {
      setSelectedDocType(null);
      setWorkflows([]);
      setSelectedWorkflows(prev => {
        const newSelected = { ...prev };
        delete newSelected[fileName];
        return newSelected;
      });
    }
  };

  // Remove file
  const removeFile = (fileName) => {
    setFiles(prev => prev.filter(file => file.name !== fileName));
    setDocumentTypes(prev => {
      const newTypes = { ...prev };
      delete newTypes[fileName];
      return newTypes;
    });
    setSelectedSystems(prev => {
      const newSystems = { ...prev };
      delete newSystems[fileName];
      return newSystems;
    });
    setSelectedPdfDocumentTypes(prev => {
      const newTypes = { ...prev };
      delete newTypes[fileName];
      return newTypes;
    });
    setSelectedFunds(prev => {
      const newFunds = { ...prev };
      delete newFunds[fileName];
      return newFunds;
    });
    setSelectedWorkflows(prev => {
      const newWorkflows = { ...prev };
      delete newWorkflows[fileName];
      return newWorkflows;
    });
    setUploadProgress(prev => {
      const newProgress = { ...prev };
      delete newProgress[fileName];
      return newProgress;
    });
    setValidationErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[`${fileName}-type`];
      delete newErrors[`${fileName}-system`];
      delete newErrors[`${fileName}-pdf-type`];
      delete newErrors[`${fileName}-fund`];
      delete newErrors[`${fileName}-workflow`];
      delete newErrors[`${fileName}-empty`];
      return newErrors;
    });
  };

  const validateFiles = () => {
    const newErrors = {};
    let isValid = true;
    const emptyFileErrors = [];
    
    files.forEach(file => {
      if (!file || file.size === 0) {
        const message = getEmptyFileMessage(file?.name || 'Selected file');
        newErrors[`${file?.name || 'unknown'}-empty`] = message;
        emptyFileErrors.push(message);
        isValid = false;
        return;
      }

      // Validation for Excel files
      if (file.type !== 'application/pdf') {
        // Reference Document Type is mandatory
        if (!documentTypes[file.name]) {
          newErrors[`${file.name}-type`] = 'Reference Data Type is required';
          isValid = false;
        }
        
        // Source GL is mandatory
        if (!selectedSystems[file.name]) {
          newErrors[`${file.name}-system`] = 'Source GL is required';
          isValid = false;
        }
      }
    });

    if (emptyFileErrors.length > 0) {
      setErrors(emptyFileErrors);
      showSnackbar(
        emptyFileErrors.length === 1
          ? emptyFileErrors[0]
          : `${emptyFileErrors.length} empty files (0 bytes) were rejected and cannot be uploaded.`,
        'error'
      );
    }
    
    setValidationErrors(newErrors);
    return isValid;
  };

  const startUpload = async () => {
    if (uploading) return;
    
    if (!validateFiles()) {
      return;
    }
    
    const token = getAuthToken();
    if (!token) {
      setErrors(['Authentication token not found. Please log in again.']);
      return;
    }
    
    setUploading(true);
    setErrors([]);
    const results = [];

    try {
      for (const file of files) {
        let fileToUpload = file;
        try {
          if (!file || file.size === 0) {
            throw new Error(getEmptyFileMessage(file?.name || 'Selected file'));
          }

          const userId = localStorage.getItem('user_id');
          let fileMeta;
          
          if (file.type === 'application/pdf') {
            const docTypeId = selectedPdfDocumentTypes[file.name];
            const selectedDocType = pdfDocumentTypes.find(doc => doc.id === docTypeId);
            const docTypeConfig = docTypeId ? documentTypeConfigs[docTypeId] : null;
            const workflowId = selectedWorkflows[file.name]?.id;
            const selectedWorkflow = workflows.find(w => w.id === workflowId);
            
            fileMeta = {
              name: file.name,  // Use original file name for PDFs
              content_type: fileToUpload.type,
              user_id: userId,
              document_type_id: selectedDocType?.documentTypeId || docTypeConfig?.documentTypeId || null,
              document_type_config_id: docTypeId || null,
              fund_id: selectedFunds[file.name] || null,
              investor_id: formData.investorId || null,
              holding_id: formData.holdingId || null,
              workflow_definition_snowflake_id: selectedWorkflow?.workflowDefinitionSnowflakeId || null,
              additional_details: additionalDetails || null,
            };
          } else {
            const selectedSystem = systems.find(
              sys => (sys.systemId || sys.system_id || sys.id) === selectedSystems[file.name]
            );
            const sourceGeneralLedger = selectedSystem 
              ? selectedSystem.name.toUpperCase().replace(/\s+/g, '_')
              : null;
            
            // Check if template is selected - if yes, convert to JSON
            const templateId = selectedTemplates[file.name];
            if (templateId) {
              try {
                // Extract mapped data from Excel
                const mappedData = await extractMappedDataFromExcel(file, templateId);
                
                // Create JSON blob
                const jsonContent = JSON.stringify(mappedData, null, 2);
                const jsonBlob = new Blob([jsonContent], { type: 'application/json' });
                
                // Create a new File object with .json extension
                const jsonFileName = file.name.replace(/\.(xlsx|xls)$/i, '.json');
                fileToUpload = new File([jsonBlob], jsonFileName, { type: 'application/json' });
                
                fileMeta = {
                  name: jsonFileName,
                  content_type: 'application/json',
                  user_id: userId,
                  document_type_id: documentTypes[file.name] || null,
                  source_general_ledger: sourceGeneralLedger,
                  fund_id: formData.fundId || null,
                  investor_id: formData.investorId || null,
                  holding_id: formData.holdingId || null,
                  system_id: selectedSystems[file.name] || null,
                  template_id: templateId,
                  user_id: userId || null,
                  additional_details: additionalDetails || null
                };
                
                console.log(`Converted ${file.name} to JSON with ${mappedData.length} mapped records`);
              } catch (error) {
                console.error('Error converting Excel to JSON:', error);
                throw new Error(`Failed to process Excel file with template: ${error.message}`);
              }
            } else {
              // Original Excel file upload if no template selected
              fileMeta = {
                name: file.name,
                content_type: file.type,
                user_id: userId,
                document_type_id: documentTypes[file.name] || null,
                source_general_ledger: sourceGeneralLedger,
                fund_id: formData.fundId || null,
                investor_id: formData.investorId || null,
                holding_id: formData.holdingId || null,
                system_id: selectedSystems[file.name] || null,
                user_id: userId || null,
                additional_details: additionalDetails || null
              };
            }
          }
          
          if (!token) {
            throw new Error('Authentication token is missing. Please log in again.');
          }
          
          if (!API_BASE) {
            throw new Error('API base URL is not configured. Please check your environment settings.');
          }
          
          if (!formData.tenantId) {
            throw new Error('Tenant ID is missing. Please select a tenant.');
          }
          
          // Create unit of work
          const uowResponse = await fetch(`${API_BASE}/${formData.tenantId}/units-of-work`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify({ 
              name: formData.unitOfWorkName, 
              documents: [fileMeta] 
            })
          });
          
          if (!uowResponse.ok) {
            throw new Error(`Failed to create unit of work for ${file.name}: ${uowResponse.status}`);
          }
          
          // Log successful file upload
          try {
            if (file.type === 'application/pdf') {
              await AuditLoggers.pdfFileUploaded(file.name);
            } else if (file.type.includes('spreadsheet') || file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
              await AuditLoggers.excelFileUploaded(file.name);
            }
          } catch (error) {
            console.error('Error logging file upload:', error);
            // Don't fail the upload if logging fails
          }
          
          const uowData = await uowResponse.json();
          results.push({
            type: 'success',
            message: `Unit of Work created for ${file.name}. ID: ${uowData.uuid}`,
            data: uowData
          });
          
          // Upload file - use the transformed file (fileToUpload) which may be JSON
          const uploadFileName = fileToUpload.name;
          const fileData = uowData.documents.find(f => f.name === uploadFileName);
          if (!fileData) {
            console.error('Available documents in UoW:', uowData.documents.map(d => d.name));
            throw new Error(`File data not found for ${uploadFileName}. Available: ${uowData.documents.map(d => d.name).join(', ')}`);
          }
          
          const totalParts = Math.ceil(fileToUpload.size / PART_SIZE);
          
          // Get presigned URLs
          const urlsResponse = await fetch(`${API_BASE}/multipart/urls`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify({
              id: fileData.id,
              tenant_id: fileData.tenant_id,
              unit_of_work_id: fileData.unit_of_work_id,
              location: fileData.location,
              upload_id: fileData.upload_id,
              name: uploadFileName,
              content_type: fileToUpload.type,
              total_parts: totalParts
            })
          });
          
          if (!urlsResponse.ok) {
            const errorText = await urlsResponse.text();
            throw new Error(`Failed to get upload URLs for ${uploadFileName}: ${errorText}`);
          }
          
          const urlsData = await urlsResponse.json();
          
          // Upload parts
          const parts = [];
          for (let i = 0; i < totalParts; i++) {
            const start = i * PART_SIZE;
            const end = Math.min(start + PART_SIZE, fileToUpload.size);
            const partBlob = fileToUpload.slice(start, end);
            const partNumber = i + 1;
            const urlObj = urlsData.urls.find(p => p.part_number === partNumber);
            
            if (!urlObj || !urlObj.url) {
              throw new Error(`Missing upload URL for part ${partNumber} of ${fileToUpload.name}`);
            }
            
            const uploadResponse = await fetch(urlObj.url, {
              method: "PUT",
              body: partBlob,
              headers: {
                'Content-Type': fileToUpload.type
              }
            });
            
            if (!uploadResponse.ok) {
              const errorText = await uploadResponse.text();
              throw new Error(`Failed to upload part ${partNumber} of ${fileToUpload.name}: ${errorText}`);
            }
            
            const etag = uploadResponse.headers.get("ETag")?.replace(/"/g, '');
            if (!etag) {
              throw new Error(`Missing ETag in response for part ${partNumber} of ${fileToUpload.name}`);
            }
            
            parts.push({ etag, part_number: partNumber });
            
            const progress = Math.round(((i + 1) / totalParts) * 100);
            setUploadProgress(prev => ({
              ...prev,
              [file.name]: progress
            }));
          }
          
          // Complete multipart upload
          const completeResponse = await fetch(`${API_BASE}/multipart/complete`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify({
              id: fileData.id,
              tenant_id: formData.tenantId,
              unit_of_work_id: fileData.unit_of_work_id,
              location: fileData.location,
              upload_id: fileData.upload_id,
              name: fileToUpload.name,
              content_type: fileToUpload.type,
              parts
            })
          });
          
          if (!completeResponse.ok) {
            const errorText = await completeResponse.text();
            throw new Error(`Failed to complete upload for ${fileToUpload.name}: ${errorText}`);
          }
          
          const completeData = await completeResponse.json();
          results.push({
            type: 'success',
            message: `Successfully uploaded ${file.name}${fileToUpload.name !== file.name ? ' (converted to JSON)' : ''}`,
            data: completeData
          });
        } catch (fileError) {
          results.push({
            type: 'error',
            message: `Failed to upload ${file.name}: ${fileError.message}`,
            data: null
          });
        }
      }
      
      setUploadResults(results);
      if (onUploadComplete) {
        onUploadComplete(results);
      }
    } catch (error) {
      setErrors([error.message]);
    } finally {
      setUploading(false);
    }
  };

  return (
    <Box sx={{ p: 2, maxHeight: '80vh', overflow: 'auto' }}>
      <Box
        sx={{
          border: '2px dashed #ddd',
          borderRadius: 2,
          p: 4,
          mb: 2,
          textAlign: 'center',
          backgroundColor: '#fafafa',
          transition: 'all 0.3s ease',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '200px',
          position: 'relative',
          '&:hover': { 
            backgroundColor: '#f0f0f0',
            borderColor: ht.green
          }
        }}
        onDrop={handleFileDrop}
        onDragOver={handleDragOver}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          hidden
          onChange={handleFileSelect}
          accept=".pdf,.doc,.docx,.txt,.xlsx,.xls"
        />
        <CloudUploadIcon sx={{ 
          fontSize: 64, 
          color: ht.green, 
          mb: 2,
          display: 'block'
        }} />
        <Typography variant="h6" sx={{ mb: 1, fontWeight: 500 }}>
          Drag your file(s) to start uploading
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          OR
        </Typography>
        <Button
          variant="contained"
          onClick={() => fileInputRef.current?.click()}
          sx={{ 
            ...harmonateProceedButtonSx,
            position: 'relative',
            zIndex: 1,
            cursor: 'pointer'
          }}
        >
          Browse files
        </Button>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Supports .pdf and .xlsx
        </Typography>
      </Box>

      {/* Selected Files Display */}
      {files.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
            Selected Files ({files.length})
          </Typography>
          {files.map((file, index) => (
            <Paper key={index} variant="outlined" sx={{ p: 2, mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <FileIcon color="primary" />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body1" sx={{ fontWeight: 500, wordBreak: 'break-word' }}>
                    {file.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {formatFileSize(file.size)}
                  </Typography>
                </Box>
                
                {/* File Type Chip */}
                <Chip 
                  label={file.type === 'application/pdf' ? 'PDF' : 'XLSX'} 
                  size="small" 
                  color="primary" 
                  variant="outlined"
                  sx={{ fontSize: '0.7rem' }}
                />
                
                {/* Progress */}
                {uploading && uploadProgress[file.name] !== undefined && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LinearProgress 
                      variant="determinate" 
                      value={uploadProgress[file.name]} 
                      sx={{ height: 6, borderRadius: 3, width: 80 }}
                    />
                    <Typography variant="caption" sx={{ fontSize: '0.7rem', minWidth: 35 }}>
                      {uploadProgress[file.name]}%
                    </Typography>
                  </Box>
                )}
                
                {/* Configure Button (only for PDF) */}
                {file.type === 'application/pdf' && (
                  <IconButton
                    size="small"
                    onClick={(e) => handleMenuOpen(e, file.name)}
                    sx={{ 
                      color: '#1976d2',
                      '&:hover': { bgcolor: '#e3f2fd' }
                    }}
                  >
                    <SettingsIcon fontSize="small" />
                  </IconButton>
                )}
                
                {/* Remove Button */}
                <IconButton
                  size="small"
                  onClick={() => removeFile(file.name)}
                  sx={{ 
                    color: '#f44336',
                    '&:hover': { bgcolor: '#ffebee' }
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
              
              {/* PDF Configuration Summary */}
              {file.type === 'application/pdf' && (selectedPdfDocumentTypes[file.name] || selectedFunds[file.name] || selectedWorkflows[file.name]) && (
                <Box sx={{ 
                  mt: 1.5, 
                  ml: 5, 
                  p: 1.5, 
                  backgroundColor: ht.listBg, 
                  borderRadius: 1,
                  borderLeft: `3px solid ${ht.green}`
                }}>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
                    {selectedPdfDocumentTypes[file.name] && (
                      <Chip 
                        size="small"
                        label={
                          <Typography variant="caption" sx={{ fontWeight: 500 }}>
                            Type: <span style={{ fontWeight: 'normal' }}>
                              {pdfDocumentTypes.find(dt => dt.id === selectedPdfDocumentTypes[file.name])?.name}
                            </span>
                          </Typography>
                        }
                        sx={{ 
                          backgroundColor: 'white',
                          border: '1px solid #e0e0e0',
                          '& .MuiChip-label': { p: '0 8px' }
                        }}
                      />
                    )}
                    
                    {category !== 'TR' && selectedFunds[file.name] && (
                      <Chip 
                        size="small"
                        label={
                          <Typography variant="caption" sx={{ fontWeight: 500 }}>
                            Fund: <span style={{ fontWeight: 'normal' }}>
                              {funds.find(f => f.id === selectedFunds[file.name])?.name}
                            </span>
                          </Typography>
                        }
                        sx={{ 
                          backgroundColor: 'white',
                          border: '1px solid #e0e0e0',
                          '& .MuiChip-label': { p: '0 8px' }
                        }}
                      />
                    )}
                    
                    {selectedWorkflows[file.name] && (
                      <Chip 
                        size="small"
                        label={
                          <Typography variant="caption" sx={{ fontWeight: 500 }}>
                            Workflow: <span style={{ fontWeight: 'normal' }}>
                              {workflows.find(w => w.id === selectedWorkflows[file.name]?.id)?.workflowDefinitionName || 'N/A'}
                            </span>
                          </Typography>
                        }
                        sx={{ 
                          backgroundColor: 'white',
                          border: '1px solid #e0e0e0',
                          '& .MuiChip-label': { p: '0 8px' }
                        }}
                      />
                    )}
                  </Box>
                </Box>
              )}
              
              {/* Excel file dropdowns (inline) */}
              {file.type !== 'application/pdf' && (
                <Box sx={{ ml: 5, mt: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flexWrap: 'nowrap', overflowX: 'auto', pb: 1 }}>
                    {/* Reference Data Type */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0, flexShrink: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', minHeight: '40px' }}>
                        {/* <Typography variant="body2" sx={{ fontWeight: 500, whiteSpace: 'nowrap', fontSize: '0.8rem' }}>
                          Reference Data Type <span style={{ color: 'red' }}>*</span>
                        </Typography> */}
                      </Box>
                      <FormControl size="small" sx={{ minWidth: 200, maxWidth: 250 }} error={!!validationErrors[`${file.name}-type`]}>
                        <InputLabel id={`type-label-${file.name}`} sx={{ 
                          fontWeight: 500, 
                          color: '#000000',
                          fontSize: '0.75rem',
                          transform: 'translate(12px, -4px) scale(0.85)',
                          backgroundColor: 'white',
                          padding: '0 2px'
                        }}>
                          Reference Data Type *
                        </InputLabel>
                        <Select
                          labelId={`type-label-${file.name}`}
                          value={documentTypes[file.name] || ''}
                          onChange={(e) => handleDocumentTypeChange(file.name, e.target.value)}
                          displayEmpty
                          sx={{ 
                            bgcolor: 'white',
                            fontSize: '0.8rem',
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#d0d0d0',
                            }
                          }}
                          disabled={loadingDocumentTypes}
                          required
                        >
                          <MenuItem value="">
                            <em>Select Reference Data Type</em>
                          </MenuItem>
                          {apiDocumentTypes
                            .filter(docType => docType.name !== 'Unidentified')
                            .map((docType) => (
                              <MenuItem key={docType.documentTypeId} value={docType.documentTypeId}>
                                {docType.name}
                              </MenuItem>
                            ))}
                        </Select>
                        {validationErrors[`${file.name}-type`] && (
                          <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block', fontSize: '0.7rem' }}>
                            {validationErrors[`${file.name}-type`]}
                          </Typography>
                        )}
                      </FormControl>
                    </Box>
                    
                    {/* Source General Ledger */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0, flexShrink: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', minHeight: '40px' }}>
                        {/* <Typography variant="body2" sx={{ fontWeight: 500, whiteSpace: 'nowrap', fontSize: '0.8rem' }}>
                          Source GL <span style={{ color: 'red' }}>*</span>
                        </Typography> */}
                      </Box>
                      <FormControl size="small" sx={{ minWidth: 150, maxWidth: 180 }} error={!!validationErrors[`${file.name}-system`]}>
                        <InputLabel id={`system-label-${file.name}`} sx={{ 
                          fontWeight: 500, 
                          color: '#000000',
                          fontSize: '0.75rem',
                          transform: 'translate(12px, -4px) scale(0.85)',
                          backgroundColor: 'white',
                          padding: '0 2px'
                        }}>
                          Source GL *
                        </InputLabel>
                        <Select
                          labelId={`system-label-${file.name}`}
                          value={selectedSystems[file.name] || ''}
                          onChange={(e) => handleSystemChange(file.name, e.target.value)}
                          displayEmpty
                          sx={{ 
                            bgcolor: 'white',
                            fontSize: '0.8rem',
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#d0d0d0',
                            }
                          }}
                          disabled={loadingSystems}
                          required
                        >
                          <MenuItem value="">
                            <em>Select Source GL</em>
                          </MenuItem>
                          {systems.map((system) => (
                            <MenuItem key={system.id} value={system.systemId || system.system_id || system.id}>
                              {system.name}
                            </MenuItem>
                          ))}
                        </Select>
                        {validationErrors[`${file.name}-system`] && (
                          <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block', fontSize: '0.7rem' }}>
                            {validationErrors[`${file.name}-system`]}
                          </Typography>
                        )}
                      </FormControl>
                    </Box>
                    
                    {/* Template Selection */}
                    {documentTypes[file.name] && selectedSystems[file.name] && (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0, flexShrink: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', minHeight: '40px' }}>
                          <Typography variant="body2" sx={{ fontWeight: 500, whiteSpace: 'nowrap', fontSize: '0.8rem' }}>
                            Template
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <FormControl size="small" sx={{ minWidth: 180, maxWidth: 220 }}>
                            <Select
                              value={selectedTemplates[file.name] || ''}
                              onChange={(e) => handleTemplateChange(file.name, e.target.value)}
                              displayEmpty
                              sx={{ fontSize: '0.8rem' }}
                              disabled={loadingTemplates}
                              error={!!validationErrors[`${file.name}-template`]}
                            >
                              <MenuItem value="">
                                <em>No template selected</em>
                              </MenuItem>
                              {(templates[file.name] || []).map((template) => (
                                <MenuItem key={template.id} value={template.id}>
                                  {template.name || `Template ${template.id}`}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => handlePreviewClick(file)}
                            disabled={!selectedTemplates[file.name]}
                            sx={{ 
                              textTransform: 'none',
                              fontSize: '0.75rem',
                              height: '32px',
                              minWidth: '80px',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            Preview
                          </Button>
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Box>
              )}
            </Paper>
          ))}
        </Box>
      )}

      {/* Configuration Menu for PDF files */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            minWidth: 400,
            maxWidth: 500,
            p: 2
          }
        }}
      >
        <Typography variant="subtitle2" sx={{ px: 2, py: 1, fontWeight: 600, color: '#000000' }}>
          Configure Document
        </Typography>
        <Divider sx={{ my: 1 }} />
        
        {currentConfigFile && (
          <Box sx={{ px: 2, py: 1 }}>
            {/* Document Type */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                Document Type
              </Typography>
              <FormControl fullWidth size="small">
                <Select
                  value={selectedPdfDocumentTypes[currentConfigFile] || ''}
                  onChange={(e) => handlePdfDocumentTypeChange(currentConfigFile, e.target.value)}
                  displayEmpty
                  sx={{ fontSize: '0.85rem' }}
                  disabled={loadingPdfDocumentTypes}
                >
                  <MenuItem value="" disabled>
                    Select Document Type
                  </MenuItem>
                  {pdfDocumentTypes
                    .filter(docType => docType.name !== 'Unidentified')
                    .map((docType) => (
                      <MenuItem key={docType.id} value={docType.id}>
                        {docType.name}
                      </MenuItem>
                    ))}
                </Select>
                {loadingPdfDocumentTypes && (
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                    <CircularProgress size={16} />
                    <Typography variant="caption" sx={{ ml: 1 }}>
                      Loading types...
                    </Typography>
                  </Box>
                )}
              </FormControl>
            </Box>

            {/* Fund - hidden for Treasury (TR) category */}
            {category !== 'TR' && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                  Fund
                </Typography>
                <Autocomplete
                  fullWidth
                  options={funds}
                  getOptionLabel={(option) => option.name + (option.code ? ` (${option.code})` : '')}
                  value={funds.find(f => f.id === selectedFunds[currentConfigFile]) || null}
                  onChange={(event, newValue) => {
                    handleFundChange(currentConfigFile, newValue ? newValue.id : '');
                  }}
                  disabled={loadingFunds}
                  loading={loadingFunds}
                  loadingText="Loading funds..."
                  noOptionsText="No funds available"
                  size="small"
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Fund"
                      variant="outlined"
                      size="small"
                      sx={{ '& .MuiInputBase-root': { height: '40px' } }}
                    />
                  )}
                />
              </Box>
            )}

            {/* Workflow */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                Workflow
              </Typography>
              <FormControl fullWidth size="small">
                <Select
                  value={selectedWorkflows[currentConfigFile]?.id || ''}
                  onChange={(e) => handleWorkflowChange(currentConfigFile, e.target.value)}
                  displayEmpty
                  sx={{ fontSize: '0.85rem' }}
                  disabled={!selectedPdfDocumentTypes[currentConfigFile] || loadingWorkflows}
                >
                  <MenuItem value="" disabled>
                    {loadingWorkflows ? 'Loading workflows...' : 'Select Workflow'}
                  </MenuItem>
                  {workflows.map((workflow) => (
                    <MenuItem key={workflow.id} value={workflow.id}>
                      {workflow.workflowDefinitionName || `Workflow ${workflow.id}`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Action Buttons */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              mt: 2, 
              pt: 2, 
              borderTop: '1px solid #eee' 
            }}>
              {files.filter(f => f.type === 'application/pdf' && f.name !== currentConfigFile).length > 0 && (
                <Button 
                  onClick={() => {
                    // Get current configuration
                    const currentDocType = selectedPdfDocumentTypes[currentConfigFile];
                    const currentFund = selectedFunds[currentConfigFile];
                    const currentWorkflow = selectedWorkflows[currentConfigFile];
                    
                    // Apply to all other PDF files
                    files.forEach(file => {
                      if (file.type === 'application/pdf' && file.name !== currentConfigFile) {
                        if (currentDocType) {
                          setSelectedPdfDocumentTypes(prev => ({
                            ...prev,
                            [file.name]: currentDocType
                          }));
                        }
                        
                        if (currentFund) {
                          setSelectedFunds(prev => ({
                            ...prev,
                            [file.name]: currentFund
                          }));
                        }
                        
                        if (currentWorkflow) {
                          setSelectedWorkflows(prev => ({
                            ...prev,
                            [file.name]: currentWorkflow
                          }));
                        }
                      }
                    });
                  }}
                  variant="outlined"
                  size="small"
                  sx={{ 
                    color: ht.text,
                    borderColor: ht.darkBlue,
                    borderRadius: ht.buttonRadius,
                    textTransform: 'none',
                    bgcolor: ht.white,
                    '&:hover': {
                      borderColor: ht.darkBlue,
                      bgcolor: ht.secondaryTab,
                    },
                  }}
                >
                  Apply to All
                </Button>
              )}
              
              <Button 
                onClick={handleMenuClose}
                variant="contained"
                size="small"
                sx={{ 
                  ...harmonateProceedButtonSx,
                  ml: 'auto'
                }}
              >
                Done
              </Button>
            </Box>
          </Box>
        )}
      </Menu>

      {/* Preview Dialog */}
      <Dialog 
        open={previewOpen} 
        onClose={() => setPreviewOpen(false)}
        maxWidth="lg"
        fullWidth
        scroll="paper"
        PaperProps={harmonateDialogPaperProps}
      >
        <DialogTitle sx={harmonateDialogTitleSx}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: 'inherit' }}>
              Preview: {previewData.fileName}
            </Typography>
            <Typography variant="subtitle2" sx={{ color: ht.white }}>
              Template: {previewData.templateName}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button 
              variant="outlined" 
              size="small"
              onClick={() => setShowDataPreview(!showDataPreview)}
              startIcon={showDataPreview ? <VisibilityOff /> : <Visibility />}
              sx={{
                color: ht.white,
                borderColor: 'rgba(255,255,255,0.55)',
                textTransform: 'none',
                height: 32,
                '&:hover': {
                  borderColor: ht.white,
                  bgcolor: 'rgba(255,255,255,0.1)',
                },
              }}
            >
              {showDataPreview ? 'View Field Mappings' : 'View Data'}
            </Button>
            <IconButton 
              edge="end" 
              onClick={() => setPreviewOpen(false)} 
              aria-label="close"
              sx={harmonateDialogCloseIconSx}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ ...harmonateDialogContentSx, ...harmonateFormFieldsSx }}>
          {showDataPreview ? (
            <Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Preview of the data from your Excel file mapped to template fields.
                  Required fields are marked with an asterisk (*).
                </Typography>
                {previewData.excelData.length > 0 && (
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    Showing {previewData.excelData.length} records
                  </Typography>
                )}
              </Box>
              
              {previewData.excelData.length > 0 ? (
                <>
                  <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: '60vh', overflow: 'auto' }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>#</TableCell>
                          {previewData.templateFields.map((field, idx) => (
                            <TableCell key={idx}>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                {field}
                                {previewData.mappings[field]?.isRequired && 
                                  <span style={{ color: 'red', marginLeft: 4 }}>*</span>
                                }
                              </Box>
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {previewData.excelData.map((row, rowIndex) => (
                          <TableRow key={rowIndex} hover>
                            <TableCell>{rowIndex + 1}</TableCell>
                            {previewData.templateFields.map((field, colIndex) => {
                              const value = row[field];
                              const isRequired = previewData.mappings[field]?.isRequired;
                              const isMissing = isRequired && (value === undefined || value === '' || value === null);
                              
                              return (
                                <TableCell 
                                  key={colIndex}
                                  sx={{ 
                                    color: isMissing ? 'error.main' : 'inherit',
                                    fontStyle: isMissing ? 'italic' : 'normal',
                                    whiteSpace: 'nowrap',
                                    maxWidth: '200px',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis'
                                  }}
                                  title={value}
                                >
                                  {value !== undefined ? String(value) : '-'}
                                </TableCell>
                              );
                            })}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  
                  {previewData.templateFields.some(field => 
                    previewData.mappings[field]?.isRequired && 
                    previewData.excelData.some(row => 
                      row[field] === undefined || row[field] === '' || row[field] === null
                    )
                  ) && (
                    <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                      * Required fields with missing values are highlighted in red
                    </Typography>
                  )}
                </>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography color="textSecondary">
                    No data available for preview
                  </Typography>
                </Box>
              )}
            </Box>
          ) : (
            <Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Map Excel columns to template fields. Required fields are marked with a red asterisk (*).
                </Typography>
              </Box>
              
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: '60vh', overflow: 'auto' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Excel Column</TableCell>
                      <TableCell>Mapped To Template Field</TableCell>
                      <TableCell width="150px">Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {previewData.excelFields.map((excelField, index) => {
                      // Check if this field is auto-mapped
                      const autoMappedTemplateField = previewData.autoMappedFields?.find(
                        tf => previewData.mappings[tf]?.excelField === excelField
                      );
                      
                      // Check if this field has a custom mapping
                      const customMappedTemplateField = customMappings[previewData.fileName]?.[excelField];
                      
                      // Determine the current mapped template field
                      const mappedTemplateField = customMappedTemplateField || autoMappedTemplateField;
                      const isMapped = !!mappedTemplateField;
                      const mapping = isMapped ? previewData.mappings[mappedTemplateField] : null;
                      const mappingType = mapping?.mappingType || 
                                       (customMappedTemplateField ? 'custom' : 
                                        autoMappedTemplateField ? 'auto' : null);
                      
                      // Check if this auto-mapping has been overridden
                      const isOverridden = mappingOverrides[previewData.fileName]?.[mappedTemplateField] === 'overridden';
                      
                      // Get available template fields for dropdown
                      const availableFields = getAvailableTemplateFields(excelField, mappedTemplateField);
                      
                      return (
                        <TableRow key={index} hover>
                          <TableCell sx={{ minWidth: '200px' }}>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {excelField}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ minWidth: '300px' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <FormControl size="small" fullWidth>
                                <Select
                                  value={mappedTemplateField || ''}
                                  onChange={(e) => handleCustomMapping(excelField, e.target.value, mappedTemplateField)}
                                  displayEmpty
                                  sx={{ 
                                    fontSize: '0.85rem', 
                                    minHeight: '36px',
                                    '& .MuiSelect-select': {
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 1
                                    }
                                  }}
                                >
                                  <MenuItem value="">
                                    <em>Select template field...</em>
                                  </MenuItem>
                                  {isMapped && (
                                    <MenuItem value="" sx={{ fontStyle: 'italic', color: 'error.main' }}>
                                      ✕ Clear mapping
                                    </MenuItem>
                                  )}
                                  <Divider />
                                  {availableFields.map(field => {
                                    const fieldMapping = currentPreview?.template?.mappings?.[field];
                                    return (
                                      <MenuItem 
                                        key={field} 
                                        value={field}
                                        sx={{
                                          display: 'flex',
                                          justifyContent: 'space-between',
                                          alignItems: 'center'
                                        }}
                                      >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                          <span>{field}</span>
                                          {fieldMapping?.isRequired && (
                                            <span style={{ color: 'red', fontSize: '0.9em' }}>*</span>
                                          )}
                                        </Box>
                                        {fieldMapping?.dataType && fieldMapping.dataType !== 'TEXT' && (
                                          <Chip 
                                            label={fieldMapping.dataType}
                                            size="small"
                                            sx={{ 
                                              height: '18px', 
                                              fontSize: '0.65rem',
                                              ml: 1
                                            }}
                                          />
                                        )}
                                      </MenuItem>
                                    );
                                  })}
                                </Select>
                              </FormControl>
                              
                              {/* Status badges */}
                              {mappingType === 'auto' && !isOverridden && (
                                <Chip
                                  label="Auto"
                                  size="small"
                                  sx={{
                                    height: '22px',
                                    fontSize: '0.65rem',
                                    bgcolor: '#e8f5e9',
                                    color: '#2e7d32',
                                    border: '1px solid #81c784',
                                    minWidth: '60px'
                                  }}
                                />
                              )}
                              {mappingType === 'custom' && (
                                <Chip
                                  label="Custom"
                                  size="small"
                                  sx={{
                                    height: '22px',
                                    fontSize: '0.65rem',
                                    bgcolor: '#e3f2fd',
                                    color: '#1565c0',
                                    border: '1px solid #64b5f6',
                                    minWidth: '60px'
                                  }}
                                />
                              )}
                              {isOverridden && (
                                <Chip
                                  label="Modified"
                                  size="small"
                                  sx={{
                                    height: '22px',
                                    fontSize: '0.65rem',
                                    bgcolor: '#fff3e0',
                                    color: '#e65100',
                                    border: '1px solid #ffb74d',
                                    minWidth: '70px'
                                  }}
                                />
                              )}
                            </Box>
                          </TableCell>
                          <TableCell width="150px">
                            <Chip 
                              label={isMapped ? "Mapped" : "Unmapped"} 
                              size="small" 
                              color={isMapped ? "success" : "default"} 
                              variant="outlined"
                              sx={{ 
                                fontSize: '0.7rem',
                                minWidth: '90px'
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              
              {previewData.excelFields.filter(f => !previewData.templateFields.includes(f)).length > 0 && (
                <Typography variant="caption" color="textSecondary" sx={{ mt: 2, display: 'block' }}>
                  Note: Unmapped fields will not be included in the upload
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Snackbar for notifications */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Error Messages */}
      {errors.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errors.map((error, index) => (
            <Typography key={index} variant="body2">
              {error}
            </Typography>
          ))}
        </Alert>
      )}

      {/* Upload Results */}
      {uploadResults.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
            Upload Results
          </Typography>
          {uploadResults.map((result, index) => (
            <Alert 
              key={index} 
              severity={result.type === 'success' ? 'success' : 'error'}
              icon={result.type === 'success' ? <CheckCircleIcon /> : <ErrorIcon />}
              sx={{ mb: 1 }}
            >
              {result.message}
            </Alert>
          ))}
        </Box>
      )}

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
        <Button
          variant="outlined"
          onClick={onClose}
          disabled={uploading}
          sx={harmonateCancelButtonSx}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={startUpload}
          disabled={files.length === 0 || uploading || disabled}
          sx={{ 
            ...harmonateProceedButtonSx,
            px: 3
          }}
        >
          {uploading ? 'Uploading...' : 'Upload'}
        </Button>
      </Box>
    </Box>
  );
};

export default MultipartFileUpload;